import { useMemo } from 'react';
import { useOrderContext } from '../../../../../providers/OrderProvider';
import OrderHorizontalProgress from './OrderHorizontalProgress';
import { normalizeStoredPaymentStatus } from '../../../../../utils/order/orderPaymentStatus';

const APPROVAL_PROGRESS_STATUSES = ['inProcessOfApproval', 'approved', 'systemApproved', 'reject'];

const getApprovalStep = (order) => {
	const status = order?.approvalStatus;
	const done = status === 'approved' || status === 'systemApproved';
	let badgeText = '待审批';
	if (status === 'inProcessOfApproval') badgeText = '审批中';
	if (status === 'approved') badgeText = '审批通过';
	if (status === 'systemApproved') badgeText = '系统审批通过';
	if (status === 'reject') badgeText = '已驳回';

	return {
		key: 'approval',
		label: '审批',
		shortLabel: '审批',
		icon: 'stamp',
		done,
		pendingText: '待审批',
		doneText: badgeText,
		badgeText,
	};
};

const getPaymentStepLabel = (order) => {
	const paymentStatus = normalizeStoredPaymentStatus(order?.paymentStatus, order);
	const isPlaceOrderPaid = Boolean(order?.isPlaceOrderPaid);
	const isDelivered = Boolean(order?.isDelivered);
	const isDeliveryPaid = Boolean(order?.isDeliveryPaid);
	const isAccepted = Boolean(order?.isAccepted);
	const isAcceptPaid = Boolean(order?.isAcceptPaid);

	if (!isPlaceOrderPaid || paymentStatus === 'waitingPayPlaceOrder') return '预付款';
	if (order?.paymentMethod === '全额支付') {
		if (paymentStatus === 'payComplete' || order?.uiHints?.isPaymentFlowComplete) return '支付完成';
		return '预付款';
	}
	if (isDelivered && (!isDeliveryPaid || paymentStatus === 'waitingPayDelivery')) return '发货款';
	if (isAccepted && (!isAcceptPaid || paymentStatus === 'waitingPayAccept')) return '验收款';
	if (paymentStatus === 'payComplete' || order?.uiHints?.isPaymentFlowComplete) return '支付完成';
	return '收款';
};

const getLogisticsStepLabel = (order) => {
	if (!order?.isDelivered) return '待发货';
	if (!order?.isAccepted) return '待验收';
	return '物流完成';
};

const isLogisticsFlowDone = (order) =>
	Boolean(order?.uiHints?.isLogisticsFlowComplete) || Boolean(order?.isDelivered && order?.isAccepted);

const isLegacyCompletedOrder = (order) => Boolean(order?.uiHints?.isLegacyCompletedBeforeFinalCostCutoff);

const isOrderEffectivelyCompleted = (order) => Boolean(order?.isCompleted) || isLegacyCompletedOrder(order);

export const buildCompletionSteps = (order) => {
	const steps = [];
	const isLegacyCompleted = isLegacyCompletedOrder(order);

	if (APPROVAL_PROGRESS_STATUSES.includes(order?.approvalStatus)) {
		steps.push(getApprovalStep(order));
	}

	const logisticsStatus = getLogisticsStepLabel(order);
	const paymentStatus = getPaymentStepLabel(order);

	steps.push({
		key: 'logistics',
		label: '物流',
		shortLabel: '物流',
		icon: 'clipboard-check',
		done: isLogisticsFlowDone(order),
		pendingText: '物流中',
		doneText: '物流完成',
		badgeText: logisticsStatus,
		tooltipColumns: [{ value: logisticsStatus }, { value: paymentStatus }],
	});

	if (!isLegacyCompleted) {
		steps.push({
			key: 'finalCost',
			label: '核验',
			shortLabel: '核验',
			icon: 'check-circle',
			done: Boolean(order?.isFinalCostConfirmed),
			pendingText: '待确认',
			doneText: '已确认',
		});
	}

	steps.push({
		key: 'orderCompleted',
		label: '完成',
		shortLabel: '完成',
		icon: 'flag',
		done: isOrderEffectivelyCompleted(order),
		pendingText: '待完成',
		doneText: '已完成',
	});

	return steps;
};

export const OrderCompletionProgressHeading = ({ onCelebration = false, isDark = false }) => {
	const { order } = useOrderContext();
	const steps = useMemo(() => (order ? buildCompletionSteps(order) : []), [order]);
	if (!order?._id) return null;
	return <OrderHorizontalProgress steps={steps} variant='modalHeader' onCelebration={onCelebration} isDark={isDark} />;
};
